import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiEnvelope, Customer, ProfileUpdate } from '../models/auth.model';
import { Address, Area, NewAddress } from '../models/address.model';

/** A vaulted card (display facts only — no card number, just the gateway token server-side). */
export interface SavedCard { id: string; gateway: string; mask: string; brand: string; isDefault: boolean; }

/** Resolved delivery zone for a map pin (POST /location/check). */
export interface LocationCheck {
  supported: boolean;
  branchId: string; branchName: string;
  subAreaId: string; subAreaName: string;
  deliveryFee: number; areaId: string;
}

/** Signed-in customer's addresses + the served-areas picker (Account/Location API). */
@Injectable({ providedIn: 'root' })
export class AccountService {
  private readonly base = environment.apiBase;

  constructor(private http: HttpClient) {}

  /** Update the signed-in customer's profile. Returns the fresh profile. */
  updateProfile(payload: ProfileUpdate): Observable<Customer> {
    return this.http
      .put<ApiEnvelope<Customer>>(`${this.base}/api/v1/account/profile`, payload)
      .pipe(map((r) => r.data));
  }

  /** Saved addresses (default first). Pass lat/lng to get per-address distance + far-away hint. */
  getAddresses(lat?: number, lng?: number): Observable<Address[]> {
    let params = new HttpParams();
    if (lat != null && lng != null) {
      params = params.set('latitude', String(lat)).set('longitude', String(lng));
    }
    return this.http
      .get<ApiEnvelope<{ count: number; addresses: Address[] }>>(`${this.base}/api/v1/account/addresses`, { params })
      .pipe(map((r) => r.data.addresses));
  }

  /** Resolve a map pin to the serving sub-area + branch (422 → not served). */
  checkLocation(lat: number, lng: number): Observable<LocationCheck> {
    return this.http
      .post<ApiEnvelope<any>>(`${this.base}/api/v1/location/check`, { latitude: lat, longitude: lng })
      .pipe(map((r) => {
        const d = r.data || {};
        return {
          supported: !!d.supported,
          branchId: d.branch?.id ?? '', branchName: d.branch?.name ?? '',
          subAreaId: d.sub_area?.id ?? '', subAreaName: d.sub_area?.name ?? '',
          deliveryFee: d.sub_area?.delivery_fee ?? 0, areaId: d.area_id ?? '',
        };
      }));
  }

  /** Served areas → sub-areas (the manual picker when location is denied). */
  getAreas(): Observable<Area[]> {
    return this.http
      .get<ApiEnvelope<{ areas: Area[] }>>(`${this.base}/api/v1/location/areas`)
      .pipe(map((r) => r.data.areas));
  }

  addAddress(payload: NewAddress): Observable<Address> {
    return this.http
      .post<ApiEnvelope<Address>>(`${this.base}/api/v1/account/addresses`, payload)
      .pipe(map((r) => r.data));
  }

  /** Edit an address (is_default can't change here — use setDefaultAddress). */
  updateAddress(id: string, payload: NewAddress): Observable<Address> {
    return this.http
      .put<ApiEnvelope<Address>>(`${this.base}/api/v1/account/addresses/${id}`, payload)
      .pipe(map((r) => r.data));
  }

  deleteAddress(id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/api/v1/account/addresses/${id}`);
  }

  /** Make an address the default ("used") one. */
  setDefaultAddress(id: string): Observable<unknown> {
    return this.http.post(`${this.base}/api/v1/account/addresses/${id}/default`, {});
  }

  /** The customer's vaulted cards (GET /account/payment-tokens). */
  getPaymentTokens(): Observable<SavedCard[]> {
    return this.http.get<ApiEnvelope<any[]>>(`${this.base}/api/v1/account/payment-tokens`).pipe(
      map((r) => (r.data || []).map((c) => ({
        id: c.id, gateway: c.gateway, mask: c.card_mask, brand: c.card_brand, isDefault: !!c.is_default,
      }))),
    );
  }

  /** Remove a saved card (DELETE /account/payment-tokens/{id}). */
  deletePaymentToken(id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/api/v1/account/payment-tokens/${id}`);
  }
}
